/**
 * Textbox Display Manager
 * Visual novel style textbox display manager
 * Renders a bottom fixed dialogue box with character name + message text
 */

import { toggleSendMicButton } from './chat-input.js';
import { addSwipeSupport } from '../../../core/utils/swipe-handlers.js';
import { parseSpriteUrl, confirmRedirectToCreateVideo } from './services/spriteUpgradeService.js';
import { HangoutState } from '../state/HangoutState.js';
import { setFormattedContent } from './text-formatting.js';
import { isMobilePortrait } from './mobile-handlers.js';

const createElement = (tag, className) => {
    const el = document.createElement(tag);
    el.className = className;
    return el;
};

const ensureStylesheetLoaded = () => {
    if (typeof document === 'undefined') return;
    if (document.querySelector('link[data-vn-textbox-css="true"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/css/vn-textbox.css';
    link.dataset.vnTextboxCss = 'true';
    document.head.appendChild(link);
};

export class TextboxDisplayManager {
    constructor() {
        this.initialized = false;
        this.container = null;
        this.box = null;
        this.nameElement = null;
        this.textElement = null;
        this.voiceButton = null;
        this.videoButton = null;
        this.activeMessages = [];
        this.lastDisplayIndex = 0;
        this.vnInput = null;
        this.inputRow = null;
        this.mode = 'text'; // 'text' | 'input'
        this.hasUserEnteredInputMode = false;
        this.vnMicSendButton = null;
        this.turnNav = {
            order: [],
            cursor: 0,
            isActive: false,
            isUserNavigating: false
        };
        this.displayedCharacterId = null;
        // Track whether VN navigation applied mobile "conversation-active" so we can safely undo it.
        this._vnAppliedConversationActive = false;
        /** @type {Set<string>} */
        this._focusSpriteLoadInFlight = new Set();
        
        // Swipe navigation state for step 2 mobile portrait
        this._swipeState = {
            cleanupFn: null,
            orientationHandler: null
        };
    }

    /**
     * Initialize the textbox display manager
     * @returns {Promise<TextboxDisplayManager>} Initialized manager instance
     */
    async initialize() {
        if (this.initialized) {
            return this;
        }

        if (typeof document === 'undefined') {
            console.warn('TextboxDisplayManager: document is not available');
            return this;
        }

        ensureStylesheetLoaded();
        this._setupContainer();
        
        // Set up orientation change listener for swipe handler management
        if (typeof window !== 'undefined') {
            this._swipeState.orientationHandler = () => this._handleOrientationChange();
            window.addEventListener('orientationchange', this._swipeState.orientationHandler);
            window.addEventListener('resize', this._swipeState.orientationHandler);
        }
        
        this.initialized = true;
        return this;
    }

    /**
     * Check if textbox system is available
     * @returns {boolean} True if textbox system is available
     */
    isAvailable() {
        return typeof document !== 'undefined' && !!this.container;
    }

    setTurnNavigation(characters, options = {}) {
        const normalized = (Array.isArray(characters) ? characters : [])
            .map((c) => ({
                id: String(c?.id ?? c?.characterId ?? ''),
                name: String(c?.name ?? c?.characterName ?? 'Character')
            }))
            .filter((c) => c.id);

        const preserveUserNavigation = options?.preserveUserNavigation === true;
        const wasNavigating = !!this.turnNav?.isUserNavigating;
        const previousDisplayedId = this.displayedCharacterId
            ? String(this.displayedCharacterId)
            : null;

        this.turnNav.order = normalized;
        this.turnNav.isActive = normalized.length > 0;

        // Keep the user's place in the round when refreshing order mid-navigation
        if (preserveUserNavigation && wasNavigating && previousDisplayedId) {
            const preservedIndex = normalized.findIndex((c) => c.id === previousDisplayedId);
            if (preservedIndex >= 0) {
                this.turnNav.cursor = preservedIndex;
                this.displayedCharacterId = previousDisplayedId;
                this.turnNav.isUserNavigating = true;
                this._renderDisplayedMessage();
                if (!options?.skipSpriteFocus) {
                    this._syncMobilePortraitCharacterFocus(this.displayedCharacterId);
                }
                return;
            }
        }

        this.turnNav.cursor = 0;
        this.turnNav.isUserNavigating = false;

        const preferredCharacterId = options?.preferredCharacterId != null
            ? String(options.preferredCharacterId)
            : null;
        const preferredIndex = preferredCharacterId
            ? normalized.findIndex((c) => c.id === preferredCharacterId)
            : -1;

        if (preferredIndex >= 0) {
            this.turnNav.cursor = preferredIndex;
            this.displayedCharacterId = preferredCharacterId;
        } else {
            this.displayedCharacterId = this.turnNav.isActive ? normalized[0].id : null;
        }

        this._renderDisplayedMessage();
        if (!options?.skipSpriteFocus) {
            this._syncMobilePortraitCharacterFocus(this.displayedCharacterId);
        }
    }

    _getDisplayedMessage() {
        if (this.displayedCharacterId) {
            const found = this.findTextboxByCharacterId(this.displayedCharacterId);
            if (found) return found;
        }
        return this.activeMessages.at(-1) || null;
    }

    _renderDisplayedMessage() {
        const message = this._getDisplayedMessage();
        if (!message) return;
        this._renderMessage(message);
        this.showCanvas();
        this._syncTurnArrowVisibility();
    }

    _syncTurnArrowVisibility() {
        // Only applies to VN step 1 (dialogue mode)
        if (this.mode !== 'text') return;
        if (!this.toTextArrow) return;

        const isTurnNavActive = !!this.turnNav?.isActive && Array.isArray(this.turnNav.order) && this.turnNav.order.length > 1;
        if (!isTurnNavActive) {
            // No multi-character navigation — hide prev arrow.
            this.toTextArrow.style.display = 'none';
            return;
        }

        // Hide prev arrow on the first character in the turn.
        if (this.turnNav.cursor <= 0) {
            this.toTextArrow.style.display = 'none';
            return;
        }

        this.toTextArrow.style.display = '';
    }

    _navigateTurn(delta) {
        if (!this.turnNav?.isActive || this.turnNav.order.length === 0) return false;
        const len = this.turnNav.order.length;
        const nextIndex = this.turnNav.cursor + delta;
        if (nextIndex < 0 || nextIndex >= len) return false;
        this.turnNav.cursor = nextIndex;
        this.turnNav.isUserNavigating = true;
        this.displayedCharacterId = this.turnNav.order[nextIndex].id;
        this._renderDisplayedMessage();
        void this._syncDisplayedMessageSprite(this.displayedCharacterId);
        return true;
    }

    /**
     * Load the stage sprite for the character's assistant message in the latest round.
     * @param {string|number} characterId
     */
    async _syncDisplayedMessageSprite(characterId) {
        if (!characterId) return;
        try {
            const { resolveMessageSpriteData } = await import('../shared/sprite-state.js');
            const { focusHangoutCharacterSprite } = await import('./message-utils.js');
            const history = HangoutState.getInstance()?.getMessages?.() || [];
            if (!Array.isArray(history) || history.length === 0) {
                this._syncMobilePortraitCharacterFocus(characterId);
                return;
            }

            let lastUserIndex = -1;
            for (let i = history.length - 1; i >= 0; i -= 1) {
                if (history[i]?.role === 'user') {
                    lastUserIndex = i;
                    break;
                }
            }
            const roundStart = lastUserIndex >= 0 ? lastUserIndex + 1 : 0;
            const normalizedId = String(characterId);

            let roundMessage = null;
            for (let i = history.length - 1; i >= roundStart; i -= 1) {
                const message = history[i];
                if (message?.role !== 'assistant') continue;
                if (String(message.characterId ?? message.character_id) !== normalizedId) continue;
                if (typeof message.content !== 'string' || !message.content.trim()) continue;
                roundMessage = message;
                break;
            }

            if (!roundMessage) {
                this._syncMobilePortraitCharacterFocus(characterId);
                return;
            }

            const spriteData = resolveMessageSpriteData(roundMessage, {
                characterId: normalizedId,
                fallbackOutfitId: roundMessage.outfitId ?? roundMessage.outfit_id ?? 0
            });

            if (spriteData.actionId != null) {
                await focusHangoutCharacterSprite(normalizedId, {
                    actionId: spriteData.actionId,
                    outfitId: spriteData.outfitId,
                    isVideo: spriteData.isVideo === true
                });
                return;
            }

            this._syncMobilePortraitCharacterFocus(characterId);
        } catch (error) {
            console.warn('TextboxDisplayManager: failed to sync displayed message sprite', error);
            this._syncMobilePortraitCharacterFocus(characterId);
        }
    }

    _syncTurnCursorToCharacterId(characterId) {
        if (!this.turnNav?.isActive) return;
        if (!characterId) return;
        if (!Array.isArray(this.turnNav.order) || this.turnNav.order.length === 0) return;

        const idx = this.turnNav.order.findIndex(c => String(c?.id) === String(characterId));
        if (idx < 0) return;
        this.turnNav.cursor = idx;
    }

    _isMobilePortrait() {
        return isMobilePortrait();
    }

    _isHiddenCharacterContainer(el) {
        return el.style.display === 'none' || getComputedStyle(el).display === 'none';
    }

    _resolveCharacterFocusTarget(characterId) {
        const candidates = document.querySelectorAll(
            `[class*="character-image-"][data-character-id="${String(characterId)}"], ` +
            `.character-image[data-character-id="${String(characterId)}"]`
        );

        /** @type {HTMLElement | null} */
        let target = null;
        let usedFallback = false;

        for (const el of candidates) {
            const htmlEl = /** @type {HTMLElement} */ (el);
            // Skip hidden containers (the original single-char container gets hidden in multi-char mode)
            if (this._isHiddenCharacterContainer(htmlEl)) continue;
            // Prefer multi-char containers (character-image-X) over original container
            if (htmlEl.className.includes('character-image-')) {
                target = htmlEl;
                break;
            }
            if (!target) target = htmlEl;
        }

        if (!target) {
            const turnIndex = Array.isArray(this.turnNav?.order)
                ? this.turnNav.order.findIndex((c) => String(c?.id) === String(characterId))
                : -1;
            const fallbackIndex = turnIndex >= 0 ? turnIndex : this.turnNav?.cursor;
            if (fallbackIndex != null && fallbackIndex >= 0) {
                const byIndex = /** @type {HTMLElement | null} */ (
                    document.querySelector(`.character-image-${fallbackIndex}`)
                );
                if (byIndex && !this._isHiddenCharacterContainer(byIndex)) {
                    target = byIndex;
                    usedFallback = true;
                }
            }
        }

        return { target, usedFallback };
    }

    _prepareCharacterFocusTarget(target) {
        // Let conversation-active / currently-speaking CSS own opacity — clear inline !important leftovers
        target.style.removeProperty('opacity');
        target.style.removeProperty('visibility');
        if (target.style.display === 'none') {
            target.style.display = 'flex';
        }

        // Reset cinematic off-screen transforms; keep mobile portrait centering
        const baseTransform = target.style.getPropertyValue('--character-transform') || 'translateX(-50%)';
        const currentTransform = target.style.transform || '';
        if (currentTransform.includes('-120vw') || currentTransform.includes('scale(0.8)')) {
            target.style.setProperty('transform', baseTransform, 'important');
        } else if (!currentTransform || currentTransform === 'none') {
            target.style.setProperty('transform', 'translateX(-50%)', 'important');
        }
        target.style.setProperty('left', '50%', 'important');
        target.style.setProperty('right', 'auto', 'important');

        target.querySelectorAll('img, video').forEach((media) => {
            const htmlMedia = /** @type {HTMLElement} */ (media);
            htmlMedia.style.opacity = '1';
            htmlMedia.style.visibility = 'visible';
        });
    }

    _getDisplayIndexForFocusTarget(target, characterId) {
        const indexMatch = String(target?.className || '').match(/character-image-(\d+)/);
        if (indexMatch) return Number.parseInt(indexMatch[1], 10);

        const turnIndex = Array.isArray(this.turnNav?.order)
            ? this.turnNav.order.findIndex((c) => String(c?.id) === String(characterId))
            : -1;
        if (turnIndex >= 0) return turnIndex;
        return Number.isInteger(this.turnNav?.cursor) ? this.turnNav.cursor : 0;
    }

    _markCharacterSpeaking(target, hangoutContainer) {
        target.classList.add('currently-speaking');
        document
            .querySelectorAll('.character-image.currently-speaking, [class*="character-image-"].currently-speaking')
            .forEach((el) => {
                if (el !== target) el.classList.remove('currently-speaking');
            });

        hangoutContainer.classList.add('conversation-active');
        hangoutContainer.classList.remove('single-character-conversation');
        this._vnAppliedConversationActive = true;
    }

    async _ensureFocusTargetHasSprite(characterId, target) {
        const normalizedId = String(characterId);
        if (this._focusSpriteLoadInFlight.has(normalizedId)) return false;
        this._focusSpriteLoadInFlight.add(normalizedId);

        try {
            const { resolveLatestCharacterSpriteData } = await import('../shared/sprite-state.js');
            const { fetchCharacterSprite } = await import('./character-manager.js');
            const { storageManager } = await import('../../../core/storage/index.js');

            const hangoutState = HangoutState.getInstance();
            const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
            const spriteData = resolveLatestCharacterSpriteData({
                messages: hangoutState?.getMessages?.() || [],
                characterId: normalizedId,
                config: hangoutConfig
            });

            if (spriteData.actionId === undefined || spriteData.actionId === null) {
                console.warn('[VN focus] No actionId in history for empty container', normalizedId);
                return false;
            }

            const displayIndex = this._getDisplayIndexForFocusTarget(target, normalizedId);
            const character = (hangoutConfig.allCharacters || []).find(
                (c) => String(c?.id) === normalizedId
            );
            const outfitId = spriteData.outfitId ?? character?.outfitId ?? 0;

            await fetchCharacterSprite(
                normalizedId,
                spriteData.actionId,
                outfitId,
                displayIndex,
                true,
                spriteData.isVideo === true,
                { allowEmotionFallback: false }
            );
            return true;
        } catch (error) {
            console.warn('[VN focus] Failed to lazy-load sprite', characterId, error);
            return false;
        } finally {
            this._focusSpriteLoadInFlight.delete(normalizedId);
        }
    }

    _syncMobilePortraitCharacterFocus(characterId) {
        if (typeof document === 'undefined') return;
        if (!characterId) return;
        if (!this._isMobilePortrait()) return;

        const hangoutContainer = document.getElementById('hangout-container');
        if (!hangoutContainer) return;

        const multiCharContainers = document.querySelectorAll('[class*="character-image-"]');
        const singleContainer =
            document.querySelector('.character-image:not([class*="character-image-"])') ||
            document.querySelector('.character-image');
        const isSingleContainerMode = !!singleContainer && multiCharContainers.length === 0;
        if (isSingleContainerMode) return;
        if (multiCharContainers.length <= 1 && !singleContainer) return;

        const { target, usedFallback } = this._resolveCharacterFocusTarget(characterId);
        if (!target) {
            console.warn('[VN focus] No character container for', characterId);
            return;
        }

        this._prepareCharacterFocusTarget(target);

        const media = target.querySelector('img[src], video[src]');
        const hasMedia = !!(media && media.getAttribute('src'));
        if (usedFallback || !hasMedia) {
            console.warn('[VN focus] Character focus target', {
                characterId,
                usedFallback,
                hasMedia,
                className: target.className,
                dataCharacterId: target.getAttribute('data-character-id')
            });
        }

        // Add speaker first to avoid a blank frame while conversation-active is on
        this._markCharacterSpeaking(target, hangoutContainer);

        if (!hasMedia) {
            void this._ensureFocusTargetHasSprite(characterId, target).then((loaded) => {
                if (!loaded) return;
                if (String(this.displayedCharacterId) !== String(characterId)) return;
                const { target: refreshedTarget } = this._resolveCharacterFocusTarget(characterId);
                if (!refreshedTarget) return;
                this._prepareCharacterFocusTarget(refreshedTarget);
                this._markCharacterSpeaking(refreshedTarget, hangoutContainer);
            });
        }
    }

    /**
     * Navigate to a different character in step 2 (input mode) on mobile portrait.
     * Changes the visible sprite without switching VN mode.
     * @param {number} delta - Direction to navigate (-1 for previous, +1 for next)
     * @returns {boolean} True if navigation occurred
     */
    _navigateCharacterInStep2(delta) {
        if (!this._isMobilePortrait()) return false;
        if (this.mode !== 'input') return false;
        if (!this.turnNav?.isActive || this.turnNav.order.length <= 1) return false;

        const len = this.turnNav.order.length;
        const nextIndex = this.turnNav.cursor + delta;
        
        // Allow wrapping for better UX
        let targetIndex;
        if (nextIndex < 0) {
            targetIndex = len - 1; // Wrap to last
        } else if (nextIndex >= len) {
            targetIndex = 0; // Wrap to first
        } else {
            targetIndex = nextIndex;
        }

        this.turnNav.cursor = targetIndex;
        this.displayedCharacterId = this.turnNav.order[targetIndex].id;
        
        // Update sprite focus without changing mode
        this._syncMobilePortraitCharacterFocus(this.displayedCharacterId);
        
        
        return true;
    }

    /**
     * Set up swipe gesture handlers for step 2 mobile portrait navigation
     * Uses shared swipe-handlers utility for consistent behavior across the app
     */
    _setupStep2SwipeHandlers() {
        if (typeof document === 'undefined') return;
        if (!this._isMobilePortrait()) return;
        if (this.turnNav.order.length <= 1) return;
        
        // Clean up any existing handlers first
        this._cleanupStep2SwipeHandlers();

        const hangoutContainer = document.getElementById('hangout-container');
        if (!hangoutContainer) return;

        // Use shared swipe handler utility - returns cleanup function
        this._swipeState.cleanupFn = addSwipeSupport(hangoutContainer, (direction) => {
            // Only navigate if still in input mode and mobile portrait
            if (this.mode !== 'input' || !this._isMobilePortrait()) return;
            // Swipe left = next character (+1), swipe right = previous character (-1)
            this._navigateCharacterInStep2(direction === 'left' ? 1 : -1);
        });

    }

    /**
     * Clean up swipe gesture handlers
     */
    _cleanupStep2SwipeHandlers() {
        if (this._swipeState.cleanupFn) {
            this._swipeState.cleanupFn();
            this._swipeState.cleanupFn = null;
        }
    }

    /**
     * Handle orientation change for swipe handlers
     * Sets up or cleans up swipe handlers based on new orientation
     */
    _handleOrientationChange() {
        if (this.mode !== 'input') return;
        
        // Small delay to let orientation settle
        setTimeout(() => {
            if (this._isMobilePortrait() && this.turnNav.order.length > 1) {
                // Now in portrait with multi-character - set up swipe if not already
                if (!this._swipeState.cleanupFn) {
                    this._setupStep2SwipeHandlers();
                }
            } else {
                // Not in portrait or single character - clean up swipe handlers
                this._cleanupStep2SwipeHandlers();
            }
        }, 100);
    }

    /**
     * Create a character message in textbox format
     * @param {string} messageText - The message text
     * @param {string} characterName - Character name
     * @param {string|number} characterId - Character ID
     * @param {Object|null} [_spriteState] - unused in VN (chatbot parity)
     * @param {{ skipSpriteFocus?: boolean }} [options]
     * @returns {Promise<Object|null>} Created textbox element or null if failed
     */
    async createMessage(messageText, characterName, characterId, _spriteState = null, options = {}) {
        if (!this.isAvailable()) {
            console.warn('TextboxDisplayManager.createMessage: container unavailable');
            return null;
        }

        const existing = this.findTextboxByCharacterId(characterId);
        const message = existing || this._buildMessageRecord(characterId, characterName);
        message.isTyping = false;
        message.setText(messageText || '');
        
        // Only render to textbox if:
        // - Turn nav is not active (single character or no navigation), OR
        // - No character is currently displayed (first to render), OR
        // - This character IS the currently displayed character
        const shouldRender = !this.turnNav.isActive || 
            !this.displayedCharacterId || 
            String(characterId) === String(this.displayedCharacterId);
        
        if (shouldRender) {
            this.displayedCharacterId = String(characterId);
            if (this.turnNav?.isActive && !this.turnNav?.isUserNavigating) {
                this._syncTurnCursorToCharacterId(this.displayedCharacterId);
            }
            this._renderMessage(message);
            
            if (!options?.skipSpriteFocus) {
                this._syncMobilePortraitCharacterFocus(characterId);
            }
        }
        this._upsertMessage(message);
        this.showCanvas();
        if (!options?.skipSpriteFocus) {
            this._maybeReturnToTextMode(message);
        }
        return message;
    }

    /**
     * Create a typing indicator for textbox
     * @param {string|number} characterId - Character ID
     * @param {string} characterName - Character name
     * @returns {Promise<Object|null>} Created typing indicator or null if failed
     */
    async createTypingIndicator(characterId, characterName) {
        if (!this.isAvailable()) {
            return null;
        }

        // Reuse existing message record to maintain object reference consistency
        const existing = this.findTextboxByCharacterId(characterId);
        const message = existing || this._buildMessageRecord(characterId, characterName);
        message.isTyping = true;
        message.setText('...');
        
        // Only render to textbox if:
        // - Turn nav is not active (single character or no navigation), OR
        // - No character is currently displayed (first to render), OR
        // - This character IS the currently displayed character
        const shouldRender = !this.turnNav.isActive || 
            !this.displayedCharacterId || 
            String(characterId) === String(this.displayedCharacterId);
        
        if (shouldRender) {
            if (!this.displayedCharacterId) this.displayedCharacterId = String(characterId);
            if (this.turnNav?.isActive && !this.turnNav?.isUserNavigating) {
                this._syncTurnCursorToCharacterId(this.displayedCharacterId);
            }
            this._renderMessage(message);
            this._syncMobilePortraitCharacterFocus(this.displayedCharacterId || characterId);
        }
        this._upsertMessage(message);
        this.showCanvas();
        return message;
    }

    /**
     * Remove typing indicator for textbox
     * @param {string|number} characterId - Character ID
     */
    async removeTypingIndicator(characterId) {
        const message = this.findTextboxByCharacterId(characterId);
        if (!message) return;
        if (!message.isTyping) return;
        message.isTyping = false;
        if ((message.text || '').trim() === '...') {
            message.setText('');
        }
        if (typeof message.markDirty === 'function') {
            message.markDirty();
        }
    }

    /**
     * Get all active textboxes
     * @returns {Array} Array of active textboxes
     */
    getActiveTextboxes() {
        return [...this.activeMessages];
    }

    /**
     * Alias for unified interface
     * @returns {Array} Active messages
     */
    getActiveMessages() {
        return this.getActiveTextboxes();
    }

    /**
     * Set active textboxes
     * @param {Array} textboxes - Array of textboxes to set as active
     */
    setActiveTextboxes(textboxes) {
        this.activeMessages = Array.isArray(textboxes) ? [...textboxes] : [];
        this._renderDisplayedMessage();
        if (!this.activeMessages.length) this.hideCanvas();
    }

    /**
     * Alias for unified interface
     * @param {Array} messages
     */
    setActiveMessages(messages) {
        this.setActiveTextboxes(messages);
    }

    /**
     * Update textbox text for a character
     * @param {string|number} characterId - Character ID
     * @param {string} newText - New text content
     */
    updateTextboxText(characterId, newText, _options = {}) {
        const message = this.findTextboxByCharacterId(characterId);
        if (!message) return;
        if (!message.isAwaitingDecision) {
            message.isTyping = false;
        }
        message.setText(newText || '');
        
        // Only render to textbox if:
        // - Turn nav is not active (single character or no navigation), OR
        // - No character is currently displayed (first to render), OR
        // - This character IS the currently displayed character
        const shouldRender = !this.turnNav.isActive || 
            !this.displayedCharacterId || 
            String(characterId) === String(this.displayedCharacterId);
        
        if (shouldRender) {
            this.displayedCharacterId = String(characterId);
            if (this.turnNav?.isActive && !this.turnNav?.isUserNavigating) {
                this._syncTurnCursorToCharacterId(this.displayedCharacterId);
            }
            this._renderMessage(message);
        }
        this._maybeReturnToTextMode(message);
    }

    /**
     * Find textbox by character ID
     * @param {string|number} characterId - Character ID
     * @returns {Object|null} Found textbox or null
     */
    findTextboxByCharacterId(characterId, _options = {}) {
        return this.activeMessages.find(msg => String(msg.characterId) === String(characterId)) || null;
    }

    /**
     * Remove textbox for a specific character
     * @param {string|number} characterId - Character ID to remove
     * @returns {boolean} True if removed, false if not found
     */
    removeTextboxByCharacterId(characterId) {
        const charIdStr = String(characterId);
        const index = this.activeMessages.findIndex(msg => String(msg.characterId) === charIdStr);
        if (index === -1) return false;

        this.activeMessages.splice(index, 1);

        // If we removed the currently displayed character, switch to another
        if (String(this.displayedCharacterId) === charIdStr) {
            this.displayedCharacterId = this.activeMessages[0]?.characterId || null;
            this._renderDisplayedMessage();
        }

        return true;
    }

    /**
     * Clear all active textboxes
     */
    clearActiveTextboxes() {
        this.activeMessages = [];
        this._clearContent();
        this.hideCanvas();
    }

    /**
     * Alias for unified interface
     */
    clearActiveMessages() {
        this.clearActiveTextboxes();
    }

    /**
     * Clear all textboxes
     */
    clearAllTextboxes() {
        this.clearActiveTextboxes();
    }

    /**
     * Alias for unified interface
     */
    clearAllMessages() {
        this.clearAllTextboxes();
    }

    /**
     * Check if there are active textboxes
     * @returns {boolean} True if there are active textboxes
     */
    hasActiveTextboxes() {
        return this.activeMessages.length > 0;
    }

    /**
     * Alias for unified interface
     * @returns {boolean}
     */
    hasActiveMessages() {
        return this.hasActiveTextboxes();
    }

    /**
     * Get character display index for textbox
     * @param {string|number} characterId - Character ID
     * @returns {number} Display index
     */
    async getCharacterDisplayIndex(characterId) {
        const index = this.activeMessages.findIndex(msg => String(msg.characterId) === String(characterId));
        if (index >= 0) return index;
        this.lastDisplayIndex += 1;
        return this.lastDisplayIndex;
    }

    /**
     * Redraw all textboxes (noop but keeps interface parity)
     */
    redrawAllMessages() {
        this._renderDisplayedMessage();
    }

    /**
     * Save positions noop for VN layout
     */
    async saveAllBubblePositions() {
        return;
    }

    /**
     * Show the textbox container
     */
    showCanvas() {
        if (!this.container) return;
        this.container.classList.remove('hidden');
    }

    /**
     * Hide the textbox container
     */
    hideCanvas() {
        if (!this.container) return;
        this.container.classList.add('hidden');
    }

    /**
     * Clean up the textbox display manager
     */
    cleanup() {
        // Clean up swipe handlers before removing container
        this._cleanupStep2SwipeHandlers();
        
        // Clean up orientation listeners
        if (typeof window !== 'undefined' && this._swipeState.orientationHandler) {
            window.removeEventListener('orientationchange', this._swipeState.orientationHandler);
            window.removeEventListener('resize', this._swipeState.orientationHandler);
            this._swipeState.orientationHandler = null;
        }
        
        if (this.container?.parentElement) {
            this.container.parentElement.removeChild(this.container);
        }
        this.initialized = false;
        this.container = null;
        this.nameElement = null;
        this.textElement = null;
        this.voiceButton = null;
        this.videoButton = null;
        this.activeMessages = [];
        if (textboxDisplayManagerInstance === this) {
            textboxDisplayManagerInstance = null;
        }
    }

    _getDisplayedSpriteDataForUpgrade() {
        const message = this._getDisplayedMessage();
        const characterId = String(this.displayedCharacterId ?? message?.characterId ?? '');
        let outfitId = null;
        let actionId = null;
        let spriteUrl = '';
        let characterGender = '';
        let actionName = '';

        // First try: read from HangoutState (most reliable source of current action/outfit)
        if (characterId) {
            const hangoutState = HangoutState.getInstance();
            const session = hangoutState.getCurrentSession();
            const character = session?.allCharacters?.find(c => String(c?.id) === characterId);
            if (character) {
                outfitId = character.outfitId || '0';
                actionId = character.spriteState?.action ?? character.actionId;
                characterGender = character.gender || '';
                actionName = character.spriteState?.action || '';
            }
        }

        // Try to get sprite URL from the visible DOM container for this character
        if (typeof document !== 'undefined' && characterId) {
            try {
                const safeAttrValue = characterId.replace(/"/g, '\\"');
                const containers = document.querySelectorAll(`[data-character-id="${safeAttrValue}"]`);
                for (const el of containers) {
                    const htmlEl = /** @type {HTMLElement} */ (el);
                    if (!htmlEl) continue;
                    if (htmlEl.style.display === 'none' || getComputedStyle(htmlEl).display === 'none') continue;
                    const img = /** @type {HTMLImageElement | null} */ (htmlEl.querySelector('img'));
                    if (img?.src) {
                        spriteUrl = img.src;
                        break;
                    }
                }
            } catch (e) {
                console.warn('[TextboxDisplayManager] Could not determine sprite URL from DOM:', e);
            }
        }

        // Fallback: parse from sprite URL if any fields are missing
        if (!characterId || actionId === null || actionId === undefined || outfitId === null || outfitId === undefined) {
            const parsed = parseSpriteUrl(spriteUrl);
            if ((outfitId === null || outfitId === undefined) && parsed.outfitId) outfitId = parsed.outfitId;
            if ((actionId === null || actionId === undefined) && parsed.actionId) actionId = parsed.actionId;
        }

        return { characterId, outfitId, actionId, spriteUrl, characterGender, actionName };
    }

    _setupContainer() {
        const existing = document.getElementById('vn-textbox-container');
        if (existing) {
            this.container = existing;
            this.nameElement = existing.querySelector('[data-vn-name]');
            this.textElement = existing.querySelector('[data-vn-text]');
            this.voiceButton = existing.querySelector('[data-vn-voice-btn]');
            this.videoButton = existing.querySelector('[data-vn-video-btn]');
            return;
        }

        const root = createElement('div', 'vn-textbox-container');
        root.id = 'vn-textbox-container';

        const header = createElement('div', 'vn-textbox-header');
        const nameSlot = createElement('div', 'vn-textbox-name');
        nameSlot.style.textAlign = 'center';
        const nameText = createElement('span', 'vn-textbox-name-text');
        nameText.dataset.vnName = 'true';
        nameSlot.appendChild(nameText);
        header.appendChild(nameSlot);

        const headerActions = createElement('div', 'vn-textbox-header-actions');

        const voiceBtn = createElement('button', 'vn-textbox-voice-btn');
        voiceBtn.type = 'button';
        voiceBtn.dataset.vnVoiceBtn = 'true';
        voiceBtn.setAttribute('aria-label', 'Play voice line');
        voiceBtn.setAttribute('tabindex', '0');
        // Use a voice icon by default (more intuitive than a raw glyph).
        voiceBtn.innerHTML = '<i class="fas fa-volume-up" aria-hidden="true"></i>';

        const handleVoiceButtonActivate = async (event) => {
            if (event?.type === 'touchend') {
                event.preventDefault();
            }
            event?.stopPropagation?.();
            const message = this._getDisplayedMessage();
            if (!message) {
                console.warn('[VN Voice Debug] No message found - early return');
                return;
            }
            // Don't generate voice for typing placeholders / empty content (prevents "regen" when text later changes).
            const rawText = (message.text || '').trim();
            if (message.isTyping || message.isReasoningPreview || rawText === '' || rawText === '...') {
                console.warn('[VN Voice Debug] Early return - isTyping:', message.isTyping, 'text empty:', rawText === '', 'text is ...:', rawText === '...');
                return;
            }
            // Avoid duplicate clicks while a generation is already in progress.
            if (message.voiceButtonState === 'loading') {
                console.warn('[VN Voice Debug] Early return - already loading');
                return;
            }
            try {
                const { handleVoiceButtonClick } = await import('./voice-utils.js');
                await handleVoiceButtonClick(message);
            } catch (error) {
                console.warn('TextboxDisplayManager: voice button failed', error);
                message.setVoiceButtonState('error');
            }
        };

        voiceBtn.addEventListener('click', handleVoiceButtonActivate);
        voiceBtn.addEventListener('touchend', handleVoiceButtonActivate, { passive: false });
        voiceBtn.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            handleVoiceButtonActivate(event);
        });

        const videoBtn = createElement('button', 'vn-textbox-voice-btn vn-textbox-video-btn');
        videoBtn.type = 'button';
        videoBtn.dataset.vnVideoBtn = 'true';
        videoBtn.setAttribute('aria-label', 'Upgrade image to video');
        videoBtn.setAttribute('tabindex', '0');
        videoBtn.innerHTML = '<i class="fas fa-video" aria-hidden="true"></i>';
        // TODO: remove display none when we add video support
        videoBtn.style.display = 'none';

        const handleVideoButtonActivate = async (event) => {
            if (event?.type === 'touchend') {
                event.preventDefault();
            }
            event?.stopPropagation?.();
            try {
                const spriteData = this._getDisplayedSpriteDataForUpgrade();
                await confirmRedirectToCreateVideo(spriteData);
            } catch (error) {
                console.warn('TextboxDisplayManager: video upgrade button failed', error);
            }
        };

        videoBtn.addEventListener('click', handleVideoButtonActivate);
        videoBtn.addEventListener('touchend', handleVideoButtonActivate, { passive: false });
        videoBtn.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            handleVideoButtonActivate(event);
        });

        headerActions.appendChild(voiceBtn);
        headerActions.appendChild(videoBtn);
        header.appendChild(headerActions);
        root.appendChild(header);

        const wrapper = createElement('div', 'vn-textbox-wrapper');
        const box = createElement('div', 'vn-textbox-box');
        this.box = box;
        box.setAttribute('role', 'region');
        box.setAttribute('aria-live', 'polite');
        box.setAttribute('tabindex', '0');
        box.setAttribute('aria-label', 'Dialogue');
        // NOTE: Step switching is intentionally ONLY via the right-arrow button.
        // (Clicking the textbox itself should not enter input mode.)

        const contentWrapper = createElement('div', 'vn-textbox-content');
        const textSlot = createElement('div', 'vn-textbox-text');
        textSlot.dataset.vnText = 'true';
        this.textElement = textSlot;

        const controls = this._buildControls();

        const toInputArrow = this._createArrowButton('vn-toggle-to-input', 'fas fa-chevron-right', async () => {
            const queue = (/** @type {any} */ (window)).conversationQueue;

            // If turn navigation isn't initialized yet, but we have queued responses, advance the queue anyway.
            if (!this._navigateTurn(1)) {
                if (queue?.isManualPacing && Array.isArray(queue.pendingResponses) && queue.pendingResponses.length > 0) {
                    queue.continueConversation();
                    return;
                }

                // End of turn: move to step 2 (input bar). Keep the last navigated sprite.
                this._setMode('input', { source: 'user', preserveStageSprite: true });
                this._focusHangoutComposer();
                return;
            }
            if (!queue?.isManualPacing) return;
            if (!Array.isArray(queue.pendingResponses) || queue.pendingResponses.length === 0) return;
            const displayed = this.displayedCharacterId;
            const nextQueued = queue.pendingResponses[0];
            if (String(nextQueued?.responseData?.characterId) === String(displayed)) {
                queue.continueConversation();
            }
        });
        const toTextArrow = this._createArrowButton('vn-toggle-to-text', 'fas fa-chevron-left', () => {
            this._navigateTurn(-1);
        });

        contentWrapper.appendChild(textSlot);
        contentWrapper.appendChild(controls);

        box.appendChild(contentWrapper);
        box.appendChild(toInputArrow);
        box.appendChild(toTextArrow);
        wrapper.appendChild(box);
        root.appendChild(wrapper);
        root.classList.add('hidden');
        document.body.appendChild(root);

        this.container = root;
        this.nameElement = nameText;
        this.voiceButton = voiceBtn;
        this.videoButton = videoBtn;
        this.vnInput = controls.querySelector('#vn-chat-input');
        this.inputRow = controls.querySelector('.vn-textbox-input-row');
        this.toInputArrow = toInputArrow;
        this.toTextArrow = toTextArrow;
        this._setMode('text');
    }

    _buildControls() {
        const controlsWrapper = createElement('div', 'vn-textbox-controls');
        const inputRow = createElement('div', 'vn-textbox-input-row');
        const vnInput = createElement('input', 'vn-textbox-input');
        vnInput.id = 'vn-chat-input';
        vnInput.setAttribute('aria-label', 'Type your message');
        vnInput.setAttribute('tabindex', '0');
        const hiddenInput = this._getHiddenChatInput();
        if (hiddenInput?.getAttribute('placeholder')) {
            vnInput.setAttribute('placeholder', hiddenInput.getAttribute('placeholder'));
        }
        vnInput.addEventListener('input', (event) => this._handleVnInputChange(event));
        vnInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this._handleSendOrMic();
            }
        });
        inputRow.appendChild(vnInput);

        const inputActions = createElement('div', 'vn-textbox-input-actions');

        // Inspire button (mirrors hangout help button behavior)
        const inspireBtn = createElement('button', 'vn-textbox-control-btn vn-hangout-inspire-btn');
        inspireBtn.type = 'button';
        inspireBtn.setAttribute('aria-label', 'Get AI inspiration');
        inspireBtn.setAttribute('title', 'Get AI inspiration');
        inspireBtn.setAttribute('tabindex', '0');
        inspireBtn.innerHTML = `<i class="fas fa-brain"></i>`;
        inspireBtn.addEventListener('click', () => this._proxyButtonClick('#help-inspire-button'));
        inspireBtn.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this._proxyButtonClick('#help-inspire-button');
        });
        inputActions.appendChild(inspireBtn);

        // Mic/Send toggle button (mirrors mobile composer: mic when empty, send when text)
        const micSendBtn = createElement('button', 'vn-textbox-control-btn vn-hangout-mic-send-btn');
        micSendBtn.type = 'button';
        micSendBtn.id = 'vn-mic-send-button';
        micSendBtn.setAttribute('aria-label', 'Voice input');
        micSendBtn.setAttribute('title', 'Voice input');
        micSendBtn.setAttribute('tabindex', '0');
        micSendBtn.innerHTML = `<i class="fas fa-microphone"></i>`;
        micSendBtn.addEventListener('click', () => this._handleSendOrMic());
        micSendBtn.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this._handleSendOrMic();
        });
        this.vnMicSendButton = micSendBtn;
        inputActions.appendChild(micSendBtn);

        inputRow.appendChild(inputActions);

        controlsWrapper.appendChild(inputRow);
        return controlsWrapper;
    }

    _buildMessageRecord(characterId, characterName) {
        const record = {
            id: `${characterId || 'unknown'}-${Date.now()}`,
            characterId,
            characterName: characterName || 'Character',
            text: '',
            isTyping: false,
            isAwaitingDecision: false,
            isReasoningPreview: false,
            voiceButtonState: 'play',
            isAudioPlaying: false,
            currentVoiceKey: null,
            element: this.container,
            nameElement: this.nameElement,
            textElement: this.textElement,
            _shouldRenderToTextbox: () => {
                // VN textbox is a single UI surface shared by all "message records".
                // During manual pacing, streaming updates arrive for characters the user may have navigated away from.
                // Only paint to the DOM when this record is currently displayed (otherwise we "fight" navigation).
                // 
                // Render if:
                // - Turn nav is not active (single character or no navigation), OR
                // - No character is currently displayed (first to render), OR
                // - This character IS the currently displayed character
                if (!this.turnNav?.isActive) return true;
                if (!this.displayedCharacterId) return true;
                return String(record.characterId) === String(this.displayedCharacterId);
            },
            setText: (value) => {
                record.text = value || '';
                const trimmed = (record.text || '').trim();
                // Do not clear isTyping while decisions are pending — text_complete owns that unlock.
                if (record.isTyping && trimmed && trimmed !== '...' && !record.isAwaitingDecision) {
                    record.isTyping = false;
                }
                if (this.textElement && record._shouldRenderToTextbox()) {
                    if (this._isTypingPlaceholder(record)) {
                        this._renderTypingPlaceholder();
                    } else {
                        this._renderPlainText(record.text);
                    }
                    this.textElement.classList.toggle('is-reasoning', record.isReasoningPreview === true);
                }
                // Streaming updates write directly via `bubble.setText(...)` in `message-utils.js`,
                // so we also hook the VN "step" auto-return here (not only in create/update methods).
                this._maybeReturnToTextMode(record);
            },
            setReasoningPreview: (enabled) => {
                record.isReasoningPreview = enabled === true;
                if (this.textElement && record._shouldRenderToTextbox()) {
                    this.textElement.classList.toggle('is-reasoning', record.isReasoningPreview);
                }
            },
            markDirty: () => {
                if (!record._shouldRenderToTextbox()) return;
                this._renderVoiceButtonState(record);
            },
            setVoiceButtonState: (state) => {
                record.voiceButtonState = state;
                if (!record._shouldRenderToTextbox()) return;
                this._renderVoiceButtonState(record);
            }
        };

        return record;
    }

    _maybeReturnToTextMode(message) {
        // VN has a two-step flow:
        // - "text" (step 1): show VN overlay so the player can read the response
        // - "input" (step 2): hide VN overlay so the real composer is clickable
        //
        // When a character response arrives while the user is in step 2, auto-return to step 1
        // so they can immediately see it (without needing the back arrow).
        if (this.mode !== 'input') return;
        if (!message || message.isTyping) return;
        const text = (message.text || '').trim();
        if (!text) return;
        this._setMode('text');
    }

    _setMode(mode, options = {}) {
        if (!this.box) return;
        const source = options?.source === 'user' ? 'user' : 'system';
        if (mode === 'input' && source !== 'user' && !this.hasUserEnteredInputMode) return;
        if (mode === 'input' && source === 'user') this.hasUserEnteredInputMode = true;
        const previousMode = this.mode;
        this.mode = mode === 'input' ? 'input' : 'text';
        this.box.classList.toggle('vn-mode-input', this.mode === 'input');
        this.box.classList.toggle('vn-mode-text', this.mode === 'text');
        if (this.container) {
            this.container.classList.toggle('vn-mode-input', this.mode === 'input');
            this.container.classList.toggle('vn-mode-text', this.mode === 'text');
        }

        // Critical: in step 2 we show the real hangout composer (which lives inside #hangout-container).
        // #hangout-container has z-index: 1 (stacking context), and the VN container is appended later in the DOM,
        // so the VN layer can sit above and swallow all clicks unless we hide/disable it in input mode.
        this._setVnOverlayBlocking(this.mode !== 'input');

        if (this.toInputArrow) {
            this.toInputArrow.style.display = this.mode === 'text' ? '' : 'none';
        }
        if (this.toTextArrow) {
            this.toTextArrow.style.display = this.mode === 'text' ? '' : 'none';
        }
        this._syncTurnArrowVisibility();
        this._setHangoutComposerVisible(this.mode === 'input');

        // Mobile portrait UX: in multi-character VN, keep only the last/selected character visible in step 2 as well.
        if (this.mode === 'input') {
            if (!options?.preserveStageSprite) {
                this._syncMobilePortraitCharacterFocus(this.displayedCharacterId);
            }
            
            // Set up swipe handlers for character navigation in step 2 (mobile portrait only)
            if (this._isMobilePortrait() && this.turnNav.order.length > 1) {
                this._setupStep2SwipeHandlers();
            }
        } else if (previousMode === 'input') {
            // Clean up swipe handlers when leaving input mode
            this._cleanupStep2SwipeHandlers();
        }
    }

    _setVnOverlayBlocking(shouldBlock) {
        if (!this.container) return;
        if (shouldBlock) {
            this.container.style.removeProperty('display');
            this.container.style.removeProperty('pointer-events');
            return;
        }
        // Step 2: hide VN overlay so the composer is clickable
        this.container.style.setProperty('display', 'none', 'important');
        this.container.style.setProperty('pointer-events', 'none', 'important');
    }

    _createArrowButton(extraClass, iconClass, handler) {
        const btn = createElement('button', `vn-toggle-arrow ${extraClass}`);
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Toggle view');
        btn.innerHTML = `<i class="${iconClass}"></i>`;
        btn.addEventListener('click', handler);
        btn.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handler();
            }
        });
        return btn;
    }

    _renderMessage(message) {
        if (!this.nameElement || !this.textElement) return;
        this.nameElement.textContent = message.characterName || 'Character';
        if (this._isTypingPlaceholder(message)) {
            this._renderTypingPlaceholder();
        } else {
            this._renderPlainText(message?.text || '');
        }
        this._renderVoiceButtonState(message);
    }

    _isTypingPlaceholder(message) {
        if (!message?.isTyping) return false;
        const text = String(message?.text || '').trim();
        return text === '' || text === '...';
    }

    _renderTypingPlaceholder() {
        if (!this.textElement) return;
        this.textElement.innerHTML = '<div class="typing-dots vn-textbox-typing-dots" aria-hidden="true"><span></span><span></span><span></span></div>';
        this.textElement.setAttribute('aria-busy', 'true');
    }

    _renderPlainText(text) {
        if (!this.textElement) return;
        setFormattedContent(this.textElement, text || '');
        this.textElement.removeAttribute('aria-busy');
    }

    _renderVoiceButtonState(message) {
        if (!this.voiceButton) return;
        const state = message.voiceButtonState || 'play';
        let label = 'Play voice line';
        let iconHtml = '<i class="fas fa-volume-up" aria-hidden="true"></i>';
        this.voiceButton.classList.toggle('vn-textbox-voice-btn--ready', state === 'ready' || state === 'playing' || state === 'pause');
        if (state === 'playing') {
            label = 'Pause voice line';
            iconHtml = '<i class="fas fa-pause" aria-hidden="true"></i>';
        } else if (state === 'loading') {
            label = 'Voice loading';
            iconHtml = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>';
        } else if (state === 'error') {
            label = 'Voice failed';
            iconHtml = '<i class="fas fa-exclamation-triangle" aria-hidden="true"></i>';
        } else if (state === 'ready' || state === 'pause') {
            label = 'Play voice line';
            iconHtml = '<i class="fas fa-play" aria-hidden="true"></i>';
        }
        this.voiceButton.setAttribute('aria-label', label);
        this.voiceButton.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        this.voiceButton.innerHTML = iconHtml;
    }

    _upsertMessage(message) {
        const existingIndex = this.activeMessages.findIndex(msg => String(msg.characterId) === String(message.characterId));
        if (existingIndex >= 0) {
            this.activeMessages[existingIndex] = message;
        } else {
            this.activeMessages.push(message);
        }
    }

    _clearContent() {
        if (this.nameElement) this.nameElement.textContent = '';
        this._renderPlainText('');
    }

    _getHiddenChatInput() {
        return document.getElementById('chat-input');
    }

    _handleVnInputChange(event) {
        const value = event?.target?.value ?? '';
        const chatInput = this._getHiddenChatInput();
        if (!chatInput) return;
        chatInput.value = value;
        chatInput.dispatchEvent(new Event('input', { bubbles: true }));
        toggleSendMicButton();
    }

    _syncVnMicSendButton() {
        if (!this.vnMicSendButton) return;
        const icon = this.vnMicSendButton.querySelector('i');
        if (!icon) return;

        const hasText = (this.vnInput?.value ?? '').trim().length > 0;
        if (hasText) {
            icon.className = 'fas fa-angle-double-right';
            this.vnMicSendButton.setAttribute('aria-label', 'Send message');
            this.vnMicSendButton.setAttribute('title', 'Send message');
            return;
        }

        icon.className = 'fas fa-microphone';
        this.vnMicSendButton.setAttribute('aria-label', 'Voice input');
        this.vnMicSendButton.setAttribute('title', 'Voice input');
    }

    _setHangoutComposerVisible(shouldShow) {
        if (typeof document === 'undefined') return;

        const chatInputContainer = document.querySelector('.chat-input-container');
        if (chatInputContainer) {
            if (shouldShow) {
                chatInputContainer.classList.remove('chat-hidden-force');
                chatInputContainer.style.display = '';
                chatInputContainer.style.setProperty('pointer-events', 'auto', 'important');
                chatInputContainer.style.setProperty('visibility', 'visible', 'important');
            } else {
                chatInputContainer.classList.add('chat-hidden-force');
                chatInputContainer.style.display = 'none';
                chatInputContainer.style.removeProperty('pointer-events');
                chatInputContainer.style.removeProperty('visibility');
            }
        }

        // Mobile floating controls must also follow VN step2 visibility, otherwise mic/send can appear in step1.
        const mobileFloatingControls = document.getElementById('mobile-floating-controls');
        if (mobileFloatingControls) {
            // CSS forces these visible on mobile via `display: flex !important`, so we must override with !important too.
            if (shouldShow) {
                mobileFloatingControls.style.removeProperty('display');
            } else {
                mobileFloatingControls.style.setProperty('display', 'none', 'important');
            }
        }

        // Also explicitly hide the floating buttons themselves (defense-in-depth against any future CSS changes).
        const micSendFloating = document.getElementById('mic-send-button-floating');
        if (micSendFloating) {
            if (shouldShow) {
                micSendFloating.style.removeProperty('display');
            } else {
                micSendFloating.style.setProperty('display', 'none', 'important');
            }
        }

        const mobileVoicePlayBtn = document.getElementById('mobile-voice-play-btn');
        if (mobileVoicePlayBtn) {
            if (shouldShow) {
                mobileVoicePlayBtn.style.removeProperty('display');
            } else {
                mobileVoicePlayBtn.style.setProperty('display', 'none', 'important');
            }
        }

        if (!shouldShow) {
            this._removeInlineVnBackButton();
            return;
        }

        // Sync mic/send icon immediately when revealing the composer
        try {
            toggleSendMicButton();
        } catch {
            // Non-fatal
        }

        this._ensureInlineVnBackButton();
        this._ensureHangoutComposerOrder();
        this._focusHangoutComposer();

        // Defensive: other code paths can re-render/move nodes after we toggle VN mode.
        // Re-assert the composer DOM order shortly after show.
        this._scheduleEnsureHangoutComposerOrder();
    }

    _scheduleEnsureHangoutComposerOrder() {
        // iOS Safari can be sensitive to timing; the input row can be re-rendered shortly after we show step 2.
        // Retry over a few frames + timeouts to ensure injected buttons stick.
        try {
            let frame = 0;
            const maxFrames = 8;
            const tick = () => {
                this._ensureHangoutComposerOrder();
                frame += 1;
                if (frame >= maxFrames) return;
                if (typeof requestAnimationFrame === 'function') {
                    requestAnimationFrame(tick);
                }
            };
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(tick);
            } else {
                setTimeout(() => this._ensureHangoutComposerOrder(), 0);
            }
        } catch {
            // ignore
        }
        setTimeout(() => this._ensureHangoutComposerOrder(), 50);
        setTimeout(() => this._ensureHangoutComposerOrder(), 150);
        setTimeout(() => this._ensureHangoutComposerOrder(), 400);
    }

    _focusHangoutComposer() {
        const chatInput = /** @type {HTMLInputElement | null} */ (document.getElementById('chat-input'));
        if (!chatInput) return;
        chatInput.style.setProperty('pointer-events', 'auto', 'important');
        chatInput.style.setProperty('visibility', 'visible', 'important');
        chatInput.focus();
        try {
            const value = chatInput.value || '';
            chatInput.setSelectionRange(value.length, value.length);
        } catch {
            // Ignore selection errors on some mobile browsers
        }
    }

    _ensureInlineVnBackButton() {
        if (typeof document === 'undefined') return;

        const chatInput = document.getElementById('chat-input');
        if (!chatInput) return;
        const inputAnchor = chatInput.closest('.chat-input-field-wrap') || chatInput;

        const row = chatInput.closest('.chat-input-row');
        if (!row) return;

        if (document.getElementById('vn-toggle-to-text-inline')) return;

        const btn = document.createElement('button');
        btn.id = 'vn-toggle-to-text-inline';
        btn.type = 'button';
        btn.className = 'vn-toggle-arrow vn-toggle-to-text vn-toggle-to-text-inline';
        btn.setAttribute('aria-label', 'Back to dialogue');
        btn.setAttribute('title', 'Back to dialogue');
        btn.setAttribute('tabindex', '0');
        btn.innerHTML = '<i class="fas fa-chevron-left"></i>';

        const handleBack = () => {
            this._setMode('text');
            // Step 2 → step 1 should return to the SAME (last/selected) character.
            // Character-to-character navigation is handled by the step-1 chevrons.
            this._syncTurnCursorToCharacterId(this.displayedCharacterId);
            this._renderDisplayedMessage();
            void this._syncDisplayedMessageSprite(this.displayedCharacterId);
            // Restore focus to the textbox so keyboard users stay oriented
            this.box?.focus?.();
        };

        btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            handleBack();
        });
        btn.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            handleBack();
        });

        row.insertBefore(btn, inputAnchor);
    }

    _removeInlineVnBackButton() {
        if (typeof document === 'undefined') return;
        const btn = document.getElementById('vn-toggle-to-text-inline');
        if (!btn) return;
        btn.parentElement?.removeChild(btn);
    }

    _ensureHangoutComposerOrder() {
        if (typeof document === 'undefined') return;

        const chatInput = /** @type {HTMLInputElement | null} */ (document.getElementById('chat-input'));
        if (!chatInput) return;

        /** @type {HTMLElement | null} */
        const row =
            /** @type {HTMLElement | null} */ (chatInput.closest('.chat-input-row')) ||
            /** @type {HTMLElement | null} */ (document.querySelector('.chat-input-container .chat-input-row'));
        if (!row) return;

        // Defensive: other code can re-render the composer row; re-ensure our inline back button.
        this._ensureInlineVnBackButton();

        const helpBtn = document.getElementById('help-inspire-button');
        if (helpBtn) {
            // Keep the real button hidden so it can't float outside the pill in VN step 2,
            // but still reuse its existing click handler via proxy.
            helpBtn.style.setProperty('display', 'none', 'important');
        }

        // Create an inline "brain" button inside the pill (same pattern as the inline back arrow).
        let inlineBrain = document.getElementById('vn-help-inspire-inline');
        if (!inlineBrain) {
            inlineBrain = document.createElement('button');
            inlineBrain.id = 'vn-help-inspire-inline';
            inlineBrain.type = 'button';
            inlineBrain.className = 'vn-help-inspire-inline';
            inlineBrain.setAttribute('aria-label', 'Get AI inspiration');
            inlineBrain.setAttribute('title', 'Get AI inspiration');
            inlineBrain.setAttribute('tabindex', '0');
            inlineBrain.innerHTML = '<i class="fas fa-brain"></i>';

            const handleInspire = () => {
                const realHelp = document.getElementById('help-inspire-button');
                if (!realHelp) return;
                // `.click()` is the most reliable way across iOS Safari versions.
                if (typeof (/** @type {any} */ (realHelp)).click === 'function') {
                    (/** @type {any} */ (realHelp)).click();
                    return;
                }
                realHelp.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
            };

            inlineBrain.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                handleInspire();
            });
            // iOS Safari sometimes prefers touchend for synthetic/proxied controls.
            inlineBrain.addEventListener('touchend', (event) => {
                event.preventDefault();
                event.stopPropagation();
                handleInspire();
            }, { passive: false });
            inlineBrain.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                handleInspire();
            });
        }

        // Ensure inline brain is immediately after the input (or input wrapper).
        const inputAnchor = chatInput.closest('.chat-input-field-wrap') || chatInput;
        if (inputAnchor.nextElementSibling !== inlineBrain) {
            row.insertBefore(inlineBrain, inputAnchor.nextSibling);
        }

        // Inline "video" button removed for now (was design-only).

        const micSendBtn = document.getElementById('mic-send-button');
        if (micSendBtn && micSendBtn.parentElement === row) {
            // Keep mic/send at the end (to the right of inline brain on desktop).
            if (row.lastElementChild !== micSendBtn) row.appendChild(micSendBtn);
        }
    }

    _handleSendOrMic() {
        const chatInput = this._getHiddenChatInput();
        if (!chatInput) return;
        const vnValue = this.vnInput?.value ?? '';
        chatInput.value = vnValue;
        chatInput.dispatchEvent(new Event('input', { bubbles: true }));
        toggleSendMicButton();

        const sendButton = document.getElementById('mic-send-button');
        if (sendButton) {
            sendButton.click();
            return;
        }

        const floatingButton = document.getElementById('mic-send-button-floating');
        if (floatingButton) {
            floatingButton.click();
        }
    }

    _proxyButtonClick(selector) {
        if (typeof document === 'undefined') return;
        const target = document.querySelector(selector);
        if (!target) return;
        target.dispatchEvent(new Event('click', { bubbles: true }));
    }
}

// Export singleton instance
let textboxDisplayManagerInstance = null;

/**
 * Get singleton instance of TextboxDisplayManager
 * @returns {Promise<TextboxDisplayManager>} Textbox display manager instance
 */
export async function getTextboxDisplayManager() {
    if (!textboxDisplayManagerInstance) {
        textboxDisplayManagerInstance = new TextboxDisplayManager();
    }
    if (!textboxDisplayManagerInstance.initialized) {
        await textboxDisplayManagerInstance.initialize();
    }
    return textboxDisplayManagerInstance;
}
